import { Fragment, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import KaiTokenAmount from './KaiTokenAmount';
import { useGetIdentityClaimHistory } from '../hooks/useGetIdentityClaimHistory';
import { cn } from '@/lib/utils';

type Props = {
  identity: string;
};

const filterOptions = ['Staking', 'Delegation'];
const pageSize = 10;

export default function IdentityClaimHistory({ identity }: Props) {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<string[]>([]);
  const {
    data: claimHistoryData,
    isLoading,
    error,
  } = useGetIdentityClaimHistory(identity);

  const claimHistoryDataFiltered = useMemo(
    () =>
      claimHistoryData?.filter(
        (claimHistoryDat: any) =>
          filters.length === 0 || filters.includes(claimHistoryDat.type),
      ),
    [claimHistoryData, filters],
  );

  const claimHistoryDataFilteredPaged = useMemo(
    () =>
      claimHistoryDataFiltered?.slice((page - 1) * pageSize, page * pageSize),
    [claimHistoryDataFiltered, page],
  );

  const toggleFilter = (filter: string) => {
    setFilters((prev) =>
      prev.includes(filter)
        ? prev.filter((f) => f !== filter)
        : [...prev, filter],
    );
    setPage(1);
  };

  const totalPages = claimHistoryDataFiltered
    ? Math.ceil(claimHistoryDataFiltered.length / pageSize)
    : 0;

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Header */}
      <div className="flex flex-row items-center w-full gap-4">
        <h2 className="text-xl font-semibold text-text-default grow">
          Rewards claim history
        </h2>

        {/* Filter Dropdown */}
        <div className="relative">
          <div className="flex items-center gap-2">
            <span className="text-sm text-text-secondary">Filter:</span>
            <div className="flex gap-2">
              {filterOptions.map((filterOption) => (
                <button
                  key={filterOption}
                  type="button"
                  onClick={() => toggleFilter(filterOption)}
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors',
                    filters.includes(filterOption)
                      ? 'bg-brand/10 text-brand border-brand/30'
                      : 'bg-bg-tertiary text-text-secondary border-divider hover:bg-bg-quaternary',
                  )}
                >
                  {filterOption}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="w-full space-y-4">
        <div className="grid grid-cols-3 gap-4">
          {/* Header Row */}
          <div className="col-span-1">
            <span className="text-sm font-medium text-text-secondary">
              Timestamp
            </span>
          </div>
          <div className="col-span-1">
            <span className="text-sm font-medium text-text-secondary">
              Type
            </span>
          </div>
          <div className="col-span-1 flex justify-end">
            <span className="text-sm font-medium text-text-secondary">
              Amount
            </span>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="col-span-3 flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-brand" />
            </div>
          )}

          {/* Error State */}
          {claimHistoryDataFilteredPaged && error && (
            <div className="col-span-3 flex justify-center py-8">
              <p className="text-text-secondary text-sm">
                Error occurred when fetching identity rewards claim history.
              </p>
            </div>
          )}

          {/* Empty State */}
          {claimHistoryDataFilteredPaged &&
            !error &&
            claimHistoryDataFilteredPaged.length === 0 && (
              <div className="col-span-3 flex justify-center py-8">
                <p className="text-text-tertiary text-sm">No results.</p>
              </div>
            )}

          {/* Data Rows */}
          {claimHistoryDataFilteredPaged &&
            !error &&
            claimHistoryDataFilteredPaged.length > 0 &&
            claimHistoryDataFilteredPaged.map((claimHistoryDat: any) => (
              <Fragment
                key={`${claimHistoryDat.timestamp}-${claimHistoryDat.type}`}
              >
                <div className="col-span-1 py-3 border-t border-divider">
                  <span className="text-sm text-text-default">
                    {new Date(
                      Number(claimHistoryDat.timestamp) * 1000,
                    ).toLocaleString()}
                  </span>
                </div>
                <div className="col-span-1 py-3 border-t border-divider">
                  <span
                    className={cn(
                      'text-sm px-2 py-0.5 rounded',
                      claimHistoryDat.type === 'Staking'
                        ? 'bg-brand/10 text-brand'
                        : 'bg-cyan-500/10 text-cyan-400',
                    )}
                  >
                    {claimHistoryDat.type}
                  </span>
                </div>
                <div className="col-span-1 py-3 border-t border-divider flex justify-end">
                  <KaiTokenAmount amount={BigInt(claimHistoryDat.amount)} />
                </div>
              </Fragment>
            ))}
        </div>

        {/* Pagination */}
        {claimHistoryDataFiltered &&
          claimHistoryDataFiltered.length > pageSize && (
            <div className="flex justify-center items-center gap-2 pt-4">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-sm rounded-lg border border-divider bg-bg-tertiary text-text-secondary hover:bg-bg-quaternary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <span className="text-sm text-text-secondary px-4">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 text-sm rounded-lg border border-divider bg-bg-tertiary text-text-secondary hover:bg-bg-quaternary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          )}
      </div>
    </div>
  );
}
